<template>
  <v-data-table
    dense
    must-sort
    :headers="headers"
    :options.sync="persist[persistenceKey].options"
    :footer-props="footerProps"
    :server-items-length="list.count"
    :items="list.items"
    :loading="$apollo.queries.list.loading"
    item-key="_id"
  >
    <template v-slot:item="{ item }">
      <tr>
        <td>{{ item.applicationNumber }}</td>
        <td>{{ item.registrationDate | formatDate }}</td>
        <td nowrap>{{ formatAbn(item.abn) }}</td>
        <td>{{ item.memberName }}</td>
        <td>{{ item.entityName }}</td>
        <td>{{ item.tradingName }}</td>
        <td>{{ item.businessName }}</td>
        <td>
          <span
            v-for="(activity, i) in item.activities"
            :key="i"
          >
            <template v-if="_.get(activity, 'details.lpaUserId')">
              {{ _.get(activity, 'details.propertyIdentificationCode') }}-{{ _.get(activity, 'details.lpaUserId') }}<!--
              --><template v-if="i < item.activities.length - 1">&#44;</template>
            </template>
          </span>
        </td>
        <td>
          <span
            v-for="(activity, i) in item.activities"
            :key="i"
          >
            {{ _.get(memberTypeMap, [activity.type, 'name'], activity.type) }}<!--
            --><template v-if="activity.subType">&nbsp;&#40;{{ _.get(memberTypeMap, [activity.type, 'subTypeMap', activity.subType, 'name'], activity.subType) }}&#41;</template><!--
            --><template v-if="i < item.activities.length - 1">&#44;</template>
          </span>
        </td>
        <td>{{ item.step }}</td>
        <td>{{ item.status }}</td>
        <td>{{ item.updatedAt | formatDate }}</td>
        <td>{{ item.memberNumber }}</td>
        <td class="text-end">
          <v-btn small icon @click="$emit('open', item)">
            <v-icon color="primary">edit</v-icon>
          </v-btn>
        </td>
      </tr>
    </template>

    <template v-slot:footer.prepend>
      <v-btn
        icon
        :loading="$apollo.queries.list.loading"
        :disabled="$apollo.queries.list.loading"
        @click="$apollo.queries.list.refresh()"
      >
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn
        text
        color="primary"
        :loading="!!csvStatus"
        :disabled="!!csvStatus || $apollo.queries.list.loading"
        @click="writeCsv"
      >
        <v-icon>save</v-icon>
        CSV
      </v-btn>
      {{ csvStatus }}
    </template>
  </v-data-table>
</template>

<script>
import gql from 'graphql-tag'
import vuejsStorage from 'vuejs-storage'
import papa from 'papaparse'
import flatten from 'flat'
import download from 'downloadjs'

import abrMixin from '../mixins/abr-mixin'
import gqlSortMixin from '../mixins/gql-sort-mixin'

export default {
  name: 'AdminRegistrationList',

  mixins: [
    abrMixin,
    gqlSortMixin
  ],

  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    persistenceKey: { // Provide this value to persist query options uniquely for different use-cases. Otherwise, a default path is used.
      type: String,
      default: 'default'
    }
  },

  storage: {
    keys: ['persist'],
    namespace: 'AdminRegistrationList',
    driver: vuejsStorage.drivers.sessionStorage
  },

  data () {
    return {
      headers: [
        {
          text: 'App number',
          value: 'applicationNumber',
          width: '6%'
        },
        {
          text: 'App Date',
          value: 'registrationDate',
          width: '6%'
        },
        {
          text: 'ABN',
          value: 'abn',
          width: '6%'
        },
        {
          text: 'Member Name',
          value: 'memberName',
          width: '10%'
        },
        {
          text: 'Entity Name',
          value: 'entityName',
          width: '10%'
        },
        {
          text: 'Trading Name',
          value: 'tradingName',
          width: '10%'
        },
        {
          text: 'Business Name',
          value: 'businessName',
          width: '10%'
        },
        {
          text: 'LPA (PIC-UserID)',
          sortable: false,
          width: '6%'
        },
        {
          text: 'Member Type',
          value: 'memberType',
          sortable: false,
          width: '6%'
        },
        {
          text: 'Step',
          value: 'step',
          sortable: false,
          width: '3%'
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false,
          width: '6%'
        },
        {
          text: 'Updated At',
          value: 'updatedAt',
          width: '6%'
        },
        {
          text: 'MSA',
          value: 'memberNumber',
          width: '6%'
        },
        {
          text: 'Actions',
          value: 'actions',
          class: 'text-end',
          sortable: false,
          width: '6%'
        }
      ],
      persist: {
        [this.persistenceKey]: { // For persisted query params, allows unique options per use-case.
          options: {
            multiSort: false,
            mustSort: true,
            sortDesc: [false],
            sortBy: ['applicationNumber'],
            page: 1,
            itemsPerPage: 25
          }
        }
      },
      footerProps: {
        'items-per-page-options': [10, 25, 50, 100, 200, 500]
      },
      list: {
        count: 0,
        items: []
      },
      memberTypes: [],
      csvStatus: null
    }
  },

  computed: {
    memberTypeMap () {
      const entries = this.memberTypes.map(o => [o.value, {
        ...o,
        subTypeMap: Object.fromEntries((o.subTypes ?? []).map(o => [o.value, o]))
      }])
      return Object.fromEntries(entries)
    }
  },

  apollo: {
    list: {
      query: gql`query SearchRegistrations ($filters: FilterFindManyRegistrationInput, $page: Int!, $perPage: Int!, $sort: SortFindManyRegistrationInput!) {
        list: registrationManyPagination(filter: $filters, page: $page, perPage: $perPage, sort: $sort) {
          count
          items
          {
            _id
            applicationNumber
            abn
            memberName
            entityName
            tradingName
            businessName
            step
            status
            updatedAt
            memberNumber
            registrationDate
            activities {
              _id
              type
              subType
              details {
                _id
                propertyIdentificationCode
                lpaUserId
              }
            }
          }
        }
      }`,
      variables () {
        const { options } = this.persist[this.persistenceKey]
        return {
          filters: this.filters,
          page: options.page,
          perPage: options.itemsPerPage,
          sort: this.gqlSort(options)
        }
      },
      debounce: 250,
      error (e) { this.$emit('error', e) }
    },

    memberTypes: {
      query: gql`query AllMemberTypes {
        memberTypes: memberTypeMany {
          _id
          name
          value
          subTypes {
            _id
            name
            value
          }
        }
      }`,
      error (e) { this.$emit('error', e) }
    }
  },

  methods: {
    async writeCsv () {
      this.csvStatus = 'Starting CSV download...'

      const pages = []
      const variables = {
        filter: this.filter,
        page: 1,
        perPage: 500
      }
      const maxPage = Math.floor(this.list.count / variables.perPage) + 1
      while (variables.page <= maxPage) {
        this.csvStatus = `Fetching page ${variables.page} of ${maxPage}...`
        await this.$apollo.queries.list.fetchMore({
          variables,
          updateQuery (_, { fetchMoreResult }) {
            pages.push(fetchMoreResult.list.items)
          }
        })
        variables.page++
      }
      this.csvStatus = 'Generating CSV...'

      const flatItems = pages.flat()
        // Flatten objects into dot notation keys for nested values
        .map(o => flatten(o))

      // Write CSV
      const csv = papa.unparse(flatItems)

      // Construct filename
      const now = new Date()
      let fileName = 'mymsa_registrations'
      fileName += `_${now.toISOString()}.csv`

      // Download CSV
      download(csv, fileName, 'text/csv')
      this.$emit('info', `File downloading as ${fileName}`)
      this.csvStatus = null
    }
  }
}
</script>
